'use client';
import CloseIcon from '@mui/icons-material/Close';
import { alpha, Box, IconButton, Typography, useTheme } from '@mui/material';

import { Avatar, SquareIcon } from '@/components';
import { EventMarket, EventOutcome, EventResponse, Market, Outcome } from '@/types';
import { formatProbability, getSpreadLine, normalizeMarketQuestion } from '@/utils';

interface TradeHeaderProps {
  event?: EventResponse | null;
  market: Market | EventMarket;
  outcome: Outcome | EventOutcome;
  onClose?: () => void;
}

export function TradeHeader({ event, market, outcome, onClose }: TradeHeaderProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const outcomeName = outcome?.name || '-';
  const genericTitlePattern = /^home\s*vs\.?\s*away$/i;

  let displayTitle = normalizeMarketQuestion(market);
  if (
    genericTitlePattern.test(displayTitle) &&
    event?.title &&
    !genericTitlePattern.test(event.title)
  ) {
    displayTitle = event.title;
  }

  const probability = outcome?.price ? formatProbability(Number(outcome.price)) : '-';
  const isSport = !!event?.gameId;

  const homeName = event?.homeTeam?.name?.toLowerCase() || '';
  const awayName = event?.awayTeam?.name?.toLowerCase() || '';
  const homeAbbr = event?.homeTeam?.abbreviation?.toLowerCase() || '';
  const awayAbbr = event?.awayTeam?.abbreviation?.toLowerCase() || '';

  const getSportDetails = () => {
    if (!isSport || !event) return null;

    const lowerName = outcomeName.toLowerCase();

    let matchingTeam = null;

    if (
      (homeName && (lowerName.includes(homeName) || homeName.includes(lowerName))) ||
      (homeAbbr && lowerName.includes(homeAbbr))
    ) {
      matchingTeam = event.homeTeam;
    } else if (
      (awayName && (lowerName.includes(awayName) || awayName.includes(lowerName))) ||
      (awayAbbr && lowerName.includes(awayAbbr))
    ) {
      matchingTeam = event.awayTeam;
    }

    return {
      abbreviation: matchingTeam?.abbreviation || '',
      color: matchingTeam?.color || '#3b82f6',
      logo: matchingTeam?.logo || event?.icon || event?.image || (market as any)?.image || '',
      gameTitle: event.title && !genericTitlePattern.test(event.title) ? event.title : displayTitle,
    };
  };

  const sportDetails = getSportDetails();

  const getOutcomeStyling = () => {
    const lowerName = outcomeName.toLowerCase();
    const isNo = lowerName === 'no' || lowerName === 'under';
    let label = outcomeName;
    let color = isNo ? theme.palette.error.main : theme.palette.success.main;

    if (isSport && event) {
      if (lowerName.includes('draw')) {
        return {
          label: 'DRAW',
          color: theme.palette.mode === 'dark' ? '#94a3b8' : '#64748b',
        };
      }

      const type = (market as any)?.sportsMarketType || (market as any)?.sportsType || '';
      const isTotal =
        type.toLowerCase().includes('total') ||
        (market as any)?.question?.toLowerCase().includes('o/u');
      const line = (market as any)?.line || (market as any)?.groupItemThreshold || '';

      if (isTotal) {
        const isOver = lowerName === 'yes' || lowerName === 'over';
        if (isOver) {
          return { label: `OVER`.toUpperCase(), color: '#359a5e' };
        }
        return { label: `UNDER`.toUpperCase(), color: '#cb3131' };
      }

      const isSpread =
        type.toLowerCase().includes('spread') ||
        type.toLowerCase().includes('handicap') ||
        type === 'puck line';

      if (!type.toLowerCase().includes('moneyline') && line && !label.includes(String(line))) {
        if (isSpread) {
          const isAway =
            (awayName && (lowerName.includes(awayName) || awayName.includes(lowerName))) ||
            (awayAbbr && lowerName.includes(awayAbbr));
          const spreadLine = getSpreadLine(
            market as any,
            isAway ? event?.awayTeam?.name : event?.homeTeam?.name,
            isAway
          );
          if (spreadLine) {
            label = `${label} ${spreadLine}`;
          } else {
            label = `${label}`;
          }
        } else {
          label = `${label}`;
        }
      }

      if (sportDetails) {
        color = sportDetails.color;
        if (isSpread && sportDetails.abbreviation) {
          const isAway =
            (awayName && (lowerName.includes(awayName) || awayName.includes(lowerName))) ||
            (awayAbbr && lowerName.includes(awayAbbr));
          const spreadLine = getSpreadLine(
            market as any,
            isAway ? event?.awayTeam?.name : event?.homeTeam?.name,
            isAway
          );
          label = spreadLine
            ? `${sportDetails.abbreviation} ${spreadLine}`
            : sportDetails.abbreviation;
        } else {
          label = sportDetails.abbreviation || label;
        }
      }
    }

    return { label: label.toUpperCase(), color };
  };

  const { label: finalOutcomeLabel, color: finalOutcomeColor } = getOutcomeStyling();
  const sanitizedLabel = finalOutcomeLabel.replace(/^Exact Score:\s*/i, '');

  if (isSport && sportDetails) {
    return (
      <Box
        sx={{
          bgcolor: 'transparent',
          p: { xs: '0.75rem', sm: '1.25rem' },
          borderTopLeftRadius: { xs: '24px', sm: '32px' },
          borderTopRightRadius: { xs: '24px', sm: '32px' },
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          position: 'relative',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <SquareIcon
            src={sportDetails.logo}
            alt={sportDetails.abbreviation || 'Team'}
            size={44}
            borderRadius="8px"
            style={{
              objectFit: 'contain',
              padding: '4px',
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1.125rem',
                lineHeight: 1.2,
                color: 'text.primary',
                pr: 4,
              }}
            >
              {sportDetails.gameTitle}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                sx={{
                  bgcolor: alpha(finalOutcomeColor, 0.1),
                  color: finalOutcomeColor,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '6px',
                  width: 'fit-content',
                  fontSize: '0.75rem',
                  fontWeight: 800,
                }}
              >
                {sanitizedLabel}
              </Box>
              <Box
                sx={{
                  bgcolor: isDark
                    ? alpha(theme.palette.text.primary, 0.05)
                    : theme.palette.action.hover,
                  color: 'text.secondary',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '100px',
                  fontSize: '0.75rem',
                  fontWeight: 800,
                }}
              >
                {probability}%
              </Box>
            </Box>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: { xs: 8, sm: 16 },
            right: { xs: 8, sm: 16 },
            width: 32,
            height: 32,
            bgcolor: isDark ? alpha(theme.palette.text.primary, 0.05) : theme.palette.action.hover,
            color: 'text.primary',
            '&:hover': {
              bgcolor: isDark
                ? alpha(theme.palette.text.primary, 0.1)
                : theme.palette.action.selected,
            },
            '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'transparent',
        p: { xs: '0.5rem', sm: '1rem' },
        borderTopLeftRadius: { xs: '24px', sm: '32px' },
        borderTopRightRadius: { xs: '24px', sm: '32px' },
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        position: 'relative',
        boxShadow: isDark ? 'none' : theme.shadows[1],
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', pr: 5 }}>
        <Box sx={{ width: { xs: 36, sm: 44 }, height: 36, flexShrink: 0 }}>
          <Avatar src={market?.image || event?.icon || event?.image} alt={displayTitle} size={36} />
        </Box>
        <Typography
          sx={{
            color: 'text.primary',
            fontSize: '1.2rem',
            fontWeight: 700,
            lineHeight: 1.3,
            letterSpacing: '-0.01em',
          }}
        >
          {displayTitle}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Box
          sx={{
            bgcolor: alpha(finalOutcomeColor, 0.1),
            color: finalOutcomeColor,
            px: 2,
            py: 0.5,
            borderRadius: '100px',
            fontSize: '0.75rem',
            fontWeight: 800,
            border: '1px solid',
            borderColor: alpha(finalOutcomeColor, 0.2),
          }}
        >
          {finalOutcomeLabel}
        </Box>

        <Box
          sx={{
            bgcolor: isDark ? alpha(theme.palette.text.primary, 0.05) : theme.palette.action.hover,
            color: 'text.secondary',
            px: 2,
            py: 0.5,
            borderRadius: '100px',
            fontSize: '0.75rem',
            fontWeight: 800,
          }}
        >
          {probability}%
        </Box>
      </Box>

      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: { xs: 12, sm: 20 },
          right: { xs: 12, sm: 20 },
          width: 36,
          height: 36,
          bgcolor: isDark ? alpha(theme.palette.text.primary, 0.05) : theme.palette.action.hover,
          color: 'text.primary',
          '&:hover': {
            bgcolor: isDark
              ? alpha(theme.palette.text.primary, 0.1)
              : theme.palette.action.selected,
          },
          '& .MuiSvgIcon-root': { fontSize: '1.25rem' },
        }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );
}
